/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from "@angular/core";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {AppSelection} from "@app/models/selection";
import {CommonState} from "@app/store/common/common.state";
import {PatientTreatmentPlanAdvState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-adv.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {DATE_FMT_8601, TrecTranslation} from "@trec/api/common";
import {ReasonerOnDemandPrescription} from "@trec/api/reasoner";
import {StoreAction} from "@trec/components/store";
import {format} from "date-fns";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {TpAdvActions} from "../../../store/patient-treatment-plan-adv.actions";

@Component({
  selector: "c3-config-edu-ondemand",
  templateUrl: "./config-edu-ondemand.component.html",
  styleUrls: ["./config-edu-ondemand.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigEduOndemandComponent
  extends ConfigSectionComponent
  implements OnInit, OnDestroy {
  // Data /////////////////////////////////////////////////////////////////////////////////////////

  @Select(PatientTreatmentPlanAdvState.onDemandPrescription)
  onDemandPrescription$: Observable<ReasonerOnDemandPrescription | null>;
  @Select(PatientTreatmentPlanAdvState.availableOnDemandDialogs)
  availableOnDemandDialogs$: Observable<TrecTranslation[] | undefined>;
  @Select(
    hasActionsExecuting([TpAdvActions.FetchOnDemandDialogs, TpAdvActions.UpdateOnDemandDialogs])
  )
  loading$: Observable<boolean>;
  @Select(CommonState.patientContext) currentContext$: Observable<
    RegistryContexts | null | undefined
  >;

  @Input()
  context: RegistryContexts[];

  // Selection //////////////////////////////////
  selection: AppSelection = {};
  private valid = new BehaviorSubject(false);
  private touched = new BehaviorSubject(false);

  get touched$(): Observable<boolean> {
    return this.touched.asObservable();
  }

  get valid$(): Observable<boolean> {
    return this.valid.asObservable();
  }

  private readonly subscr = new Subscription();

  constructor(private store: Store, private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // Fetch available list and current on-demand prescriptions
    this.store.dispatch(new TpAdvActions.FetchOnDemandDialogs());

    // Select all
    this.subscr.add(
      this.onDemandPrescription$.subscribe((prescription) => {
        if (prescription) {
          // Set as selected all features contained in the user entry
          this.selection = prescription.dialogIds.reduce((a, dId) => ({...a, [dId]: true}), {});

          // Once fetched cofing -> its valid
          this.valid.next(true);

          this.cd.markForCheck();
        }
      })
    );
  }

  /**
   * Callback called when user select/deselect a feature.
   *
   * @param event Event
   */

  onSelectionChange(event: CustomEvent) {
    this.touched.next(true);
    this.valid.next(true);

    this.selection = {...this.selection, [event.detail.value]: !!event.detail.checked};
  }

  prepareSaveDataAction(): StoreAction | null {
    const currentPrescription = this.store.selectSnapshot(
      PatientTreatmentPlanAdvState.onDemandPrescription
    );
    const ue = this.store.selectSnapshot(CommonState.patientUserEntry);
    const context = this.store.selectSnapshot(CommonState.patientContext) as string[];

    if (!(ue && ue[0].appPrescription)) {
      throw new Error(`Try to update on demand prescription but patient user entry is not present`);
    }
    if (!context) {
      throw new Error(`Try to update on demand prescription but context is not present`);
    }

    const dialogIds: string[] = [];

    const isNew = !currentPrescription;
    const newPrescription: ReasonerOnDemandPrescription = currentPrescription
      ? {...currentPrescription}
      : {
        trecUserId: ue[0].patientId,
        context,
        dialogIds: dialogIds,
        startDate: ue[0].appPrescription ? format(new Date(ue[0].appPrescription.start), DATE_FMT_8601) : "",
        endDate: ue[0].appPrescription ? format(new Date(ue[0].appPrescription?.end), DATE_FMT_8601) : "",
      };

    // Update dialogIds array with the selected ones
    newPrescription.dialogIds = Object.entries(this.selection)
      .filter((e) => e[1]) // Keep only selected ones
      .map((e) => e[0]); // Map to dialog id

    return new TpAdvActions.UpdateOnDemandDialogs({prescription: newPrescription, isNew});
  }

  resetTouched(): void {
    this.touched.next(false);
  }

  ngOnDestroy() {
    this.subscr.unsubscribe();

    this.valid.complete();
    this.touched.complete();
  }
}
